import { useQuery } from '@tanstack/react-query'
import { apiRequest } from '../api/client'

export type MaintenanceBannerSeverity = 'info' | 'warning' | 'critical'

export interface MaintenanceBannerConfig {
  enabled: boolean
  message: string
  severity: MaintenanceBannerSeverity
  dismissible: boolean
  version: string
}

export interface PublicConfig {
  maintenanceBanner: MaintenanceBannerConfig
}

interface PublicConfigWire {
  maintenance_banner: {
    enabled: boolean
    message: string
    severity: MaintenanceBannerSeverity
    dismissible: boolean
    version: string
  }
}

function parseEnvBool(raw: string | undefined): boolean {
  if (!raw) return false
  return ['1', 'true', 'yes', 'on'].includes(raw.trim().toLowerCase())
}

function coerceSeverity(raw: string | undefined): MaintenanceBannerSeverity {
  const normalized = (raw ?? '').trim().toLowerCase()
  if (normalized === 'info') return 'info'
  if (normalized === 'critical') return 'critical'
  return 'warning'
}

function envFallback(): PublicConfig {
  const message = (import.meta.env.VITE_MAINTENANCE_BANNER_MESSAGE ?? '').trim()
  const severity = coerceSeverity(import.meta.env.VITE_MAINTENANCE_BANNER_SEVERITY)
  return {
    maintenanceBanner: {
      enabled:
        parseEnvBool(import.meta.env.VITE_MAINTENANCE_BANNER_ENABLED) &&
        message.length > 0,
      message,
      severity,
      dismissible: severity !== 'critical',
      version: import.meta.env.VITE_MAINTENANCE_BANNER_VERSION ?? 'env-fallback',
    },
  }
}

function normalize(wire: PublicConfigWire): PublicConfig {
  const banner = wire.maintenance_banner
  const severity = coerceSeverity(banner?.severity)
  const message = typeof banner?.message === 'string' ? banner.message : ''
  return {
    maintenanceBanner: {
      enabled: banner?.enabled === true && message.trim().length > 0,
      message,
      severity,
      // critical banners can never be dismissed regardless of server value
      dismissible:
        severity === 'critical' ? false : banner?.dismissible !== false,
      version:
        typeof banner?.version === 'string' && banner.version.length > 0
          ? banner.version
          : '1',
    },
  }
}

async function fetchPublicConfig(): Promise<PublicConfig> {
  const wire = await apiRequest<PublicConfigWire>('/public-config')
  return normalize(wire)
}

export function usePublicConfig(): PublicConfig | undefined {
  const query = useQuery({
    queryKey: ['public-config'],
    queryFn: fetchPublicConfig,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  })

  if (query.data) return query.data
  if (query.isError) return envFallback()
  return undefined
}
